import React, { useState, useEffect, useCallback } from "react";
import { useDispatch } from "react-redux";
import Button from "src/components/common/Button";
import { QRCodeSVG } from "qrcode.react";
import { SpinnerActions } from "src/redux/Spinner/actions";
import styles from "./styles.module.scss";

export default () => {
    const dispatch = useDispatch();

    const [qrCode, setQRCode] = useState("");
    const [setupSession, setSetupSession] = useState("");
    const [mfaCode, setMFACode] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [error, setError] = useState(null);

    const newQRChallenge = useCallback(async () => {
        try {
            const resp = await fetch("/auth/mfa/setup-totp", {
                method: "POST",
                credentials: "include",
            });

            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                setError(data.error || "Failed to generate QR code");
                return;
            }

            const data = await resp.json();
            setQRCode(data.otpauth_uri);
            setSetupSession(data.setup_session);
            setError(null);
        } catch (err) {
            setError("Failed to set up TOTP. Please try again.");
        }
    }, []);

    useEffect(() => {
        newQRChallenge();
    }, []);

    useEffect(() => {
        if (mfaCode.length === 0) {
            setError("Please input MFA Code");
            setDisabled(true);
            return;
        }

        if (mfaCode.length !== 6 || isNaN(Number(mfaCode))) {
            setError("MFA code must be a 6-digit number");
            setDisabled(true);
            return;
        }
        setError(null);

        setDisabled(false);
    }, [mfaCode]);

    const handleSubmitClick = async (e) => {
        e.preventDefault();
        dispatch(SpinnerActions.Open());

        try {
            const resp = await fetch("/auth/mfa/verify-totp", {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    setup_session: setupSession,
                    code: mfaCode,
                }),
            });

            const data = await resp.json();

            if (!resp.ok) {
                setError(data.error || "Invalid code. Please try again.");
                return;
            }

            setError(null);
            // TOTP registered. verify-totp upgraded the session server-side (any
            // pending-MFA gate is cleared), so reload to boot the app against the
            // now-full session. For a hard-enforced login (#33) the app was
            // intentionally NOT booted before enrollment, so this is what actually
            // lets the user in; for a voluntary setup it is a harmless one-time reboot.
            window.location.reload();
        } catch (err) {
            setError("Invalid code. Please try again.");
        } finally {
            dispatch(SpinnerActions.Close());
        }
    };

    return (
        <div className={styles.mfa}>
            <h1>Set up MFA using TOTP</h1>

            <div className={styles.splitter} />

            <div className={styles.body}>
                <div className={styles.label}>
                    <label>
                        To activate two-factor authentication using time-based
                        one-time passwords, scan the below QR code with your
                        authenticator app and type in the MFA code below. If you
                        don't have an authenticator app, we recommend either
                        Google Authenticator or Authy.
                    </label>
                </div>

                {qrCode.length > 0 && (
                    <QRCodeSVG
                        size={200}
                        level="L"
                        includeMargin={true}
                        value={qrCode}
                    />
                )}

                <div className={styles.qr}>
                    <Button
                        value="New QR Code"
                        onClick={newQRChallenge}
                        dataTestId="new-qr-button"
                    />
                </div>

                <div className={styles.mfa_code}>
                    <label>MFA Code:</label>
                </div>

                <div className={styles.body.label}>
                    <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        autoCapitalize="off"
                        maxLength="6"
                        spellCheck="false"
                        autoCorrect="off"
                        autoComplete="one-time-code"
                        placeholder=""
                        value={mfaCode}
                        onChange={(e) => setMFACode(e.target.value)}
                        data-testid="mfa-code"
                    />
                </div>

                <div className={styles.footer}>
                    <Button
                        value="Submit"
                        onClick={handleSubmitClick}
                        dataTestId="submit-button"
                        disabled={disabled}
                    />
                    {error && (
                        <div className={styles.error} data-testid="error-div">
                            {error}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
